import React, { useState, useEffect, useCallback } from "react";
import { ClipboardCheck, CalendarCheck, CalendarDays } from "lucide-react";
import Skeleton from "../../common/Skeleton";
import NoDataFound from "../../common/NoDataFound";
import Toast, { type ToastMessage } from "../../common/Toast";
import { useAuth } from "../../../contexts/AuthContext";
import { usePermission, NOT_AUTHORISED_MSG } from "../../../contexts/PermissionContext";
import { listLeaves, listDays, listHolidays } from "../../../services/hrService";
import { Holiday } from "../../../services/hrTypes";

const RBAC_ACTIVITY = "HR_LEAVES";

// Read-only HR dashboard. All data flows through hrService only.
const HrOverview: React.FC = () => {
    const { state } = useAuth();
    const currentUserId = Number(state.user?.id);

    // Read-only dashboard — view-gated only (no maker-checker dot). The headline
    // metric is pending leave approvals, so it gates on the HR_LEAVES activity.
    const viewPerm = usePermission(RBAC_ACTIVITY, "view");
    const canView = viewPerm.allowed;
    const permLoading = viewPerm.loading;

    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const [pendingApprovals, setPendingApprovals] = useState(0);
    const [todayMarks, setTodayMarks] = useState(0);
    const [upcomingHolidays, setUpcomingHolidays] = useState<Holiday[]>([]);

    const fetchAll = useCallback(async () => {
        if (!canView) { setLoading(false); return; }
        setLoading(true);
        try {
            const today = new Date().toISOString().slice(0, 10);
            const year = new Date().getFullYear();
            const [leaves, days, holidays] = await Promise.all([
                listLeaves(),
                listDays(today, today),
                listHolidays(year),
            ]);

            setPendingApprovals(
                leaves.filter(
                    (l) => l.workflow_state === "submitted" && l.approver_user_id === currentUserId,
                ).length,
            );
            setTodayMarks(days.length);
            setUpcomingHolidays(
                holidays
                    .filter((h) => h.holiday_date >= today)
                    .sort((a, b) => a.holiday_date.localeCompare(b.holiday_date))
                    .slice(0, 5),
            );
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Failed to load" });
        } finally {
            setLoading(false);
        }
    }, [currentUserId, canView]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const fmtDate = (d: string) =>
        new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

    const CountCard: React.FC<{
        icon: React.ElementType;
        label: string;
        value: number;
        testid: string;
    }> = ({ icon: Icon, label, value, testid }) => (
        <div className="action-card" data-testid={testid}>
            <div className="action-card__header">
                <div className="action-card__icon"><Icon size={16} /></div>
                <div className="action-card__title-block">
                    <span className="action-card__title" style={{ fontSize: "1.5rem", fontWeight: 700 }}>{value}</span>
                    <span className="action-card__subtitle">{label}</span>
                </div>
            </div>
        </div>
    );

    // ── Not-authorised gate ────────────────────────────────────────────────────
    if (!permLoading && !canView) {
        return (
            <div className="netbody-card flex flex-col h-full">
                <NoDataFound title="Not authorised" message={viewPerm.message || NOT_AUTHORISED_MSG} />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header" />

            <div className="netbody-content">
                <div className="scroll-container-nofilters">
                    <div className="action-card-grid action-card-grid--compact">
                        {loading ? (
                            <>
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <div key={i} className="action-card action-card--skeleton">
                                        <div className="action-card__skel-header">
                                            <Skeleton.Box width={34} height={34} radius={8} />
                                            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                                                <Skeleton.Box width="40%" height={18} />
                                                <Skeleton.Box width="60%" height={10} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        ) : (
                            <>
                                <CountCard icon={ClipboardCheck} label="Pending approvals" value={pendingApprovals} testid="CARD_hr_pending_approvals" />
                                <CountCard icon={CalendarCheck} label="Today's attendance marks" value={todayMarks} testid="CARD_hr_today_marks" />
                                <CountCard icon={CalendarDays} label="Upcoming holidays" value={upcomingHolidays.length} testid="CARD_hr_upcoming_holidays" />
                            </>
                        )}
                    </div>

                    {!loading && (
                        <div data-testid="SECTION_hr_upcoming_holidays" style={{ marginTop: 16 }}>
                            <div style={{ fontWeight: 600, fontSize: "0.875rem", marginBottom: 8 }}>Upcoming Holidays</div>
                            {upcomingHolidays.length === 0 ? (
                                <NoDataFound title="No upcoming holidays" message="No holidays scheduled for the rest of the year." />
                            ) : (
                                <div className="table-list">
                                    <table className="table-list-table">
                                        <thead>
                                            <tr>
                                                <th className="table-list-th table-list-cell--sno">S.No</th>
                                                <th className="table-list-th">Holiday</th>
                                                <th className="table-list-th">Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {upcomingHolidays.map((h, idx) => (
                                                <tr key={h.id} className="table-list-row" data-testid={`ROW_hr_holiday_${h.id}`}>
                                                    <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                                                    <td className="table-list-cell"><span className="table-list-cell-title">{h.name}</span></td>
                                                    <td className="table-list-cell">{fmtDate(h.holiday_date)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default HrOverview;
